import { reviewSignatureStrokes, signatureBounds, type SignatureStroke } from '@hue/contracts';
import {
  Button,
  CameraCapture,
  Modal,
  Select,
  SignaturePad,
  StatusPill,
  cameraAvailable,
  useSnackbar,
  type PadPointer,
  type PadStroke,
} from '@hue/design-system';
import { useQuery } from '@tanstack/react-query';
import { Camera, Expand, FileDown, FileText, Minimize, Paperclip, PenLine } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import {
  getApiError,
  recordsApi,
  type ClientChartTemplate,
  type ClinicalDocumentScope,
  type ClinicalDocumentSummary,
  type DocumentProfessionals,
  type ProfessionalChoice,
} from '../../lib/api.js';
import { formatDateTime } from '../../lib/time-zone.js';
import { cn, documentsOf, pdfName, saveFile } from './helpers.js';

const ACCEPTED = 'application/pdf,image/jpeg,image/png,image/webp';
const BLANK = 'blank';
const SELF = 'self';

/// A situação da assinatura de uma versão, do jeito que a ficha, o cartão e a lista mostram. Só o
/// documento da ficha tira a pendência: o prontuário assinado não responde pela versão nova.
export function SignaturePill({
  template,
  versionId,
  documents,
}: {
  template: Pick<ClientChartTemplate, 'signaturePolicy'>;
  versionId: string;
  documents: readonly ClinicalDocumentSummary[];
}) {
  const own = documentsOf(documents, versionId).find((document) => document.kind === 'RECORD');
  if (own?.method === 'DIGITAL') return <StatusPill tone="positive">Assinada na tela</StatusPill>;
  if (own) return <StatusPill tone="positive">Via física anexada</StatusPill>;
  return template.signaturePolicy === 'EVERY_VERSION' ? (
    <StatusPill tone="warning">Assinatura pendente</StatusPill>
  ) : (
    <StatusPill tone="neutral">Sem assinatura</StatusPill>
  );
}

/// Os traços gravados, redesenhados em vetor e enquadrados: a assinatura aparece igual em qualquer
/// tela, e a cor acompanha o tema.
export function SignatureImage({
  strokes,
  label = 'Assinatura',
  className,
}: {
  strokes: readonly SignatureStroke[];
  label?: string;
  className?: string;
}) {
  const box = signatureBounds(strokes);
  const scale = 1000 / box.width;
  const at = ([x, y]: [number, number]) => [(x - box.x) * scale, (y - box.y) * scale] as const;
  const place = (point: [number, number]) =>
    at(point)
      .map((value) => value.toFixed(1))
      .join(',');
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 1000 ${Math.round(box.height * scale)}`}
      className={cn('h-auto w-full max-w-[20rem] text-content', className)}
    >
      {strokes.map((stroke, index) =>
        stroke.length === 1 ? (
          <circle
            key={index}
            cx={at(stroke[0]!)[0]}
            cy={at(stroke[0]!)[1]}
            r="2"
            fill="currentColor"
          />
        ) : (
          <polyline
            key={index}
            points={stroke.map(place).join(' ')}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        ),
      )}
    </svg>
  );
}

/// Um documento assinado, com o que a tela interna de integridade mostra: os dois traços, quem
/// colheu, o aparelho, a unidade e o resumo SHA-256 (ADR 0040, decisão 10 — nada disso vai ao papel).
export function DocumentCard({
  document,
  clientName,
  timeZone,
}: {
  document: ClinicalDocumentSummary;
  clientName: string;
  timeZone: string;
}) {
  const snackbar = useSnackbar();
  const [downloading, setDownloading] = useState(false);
  const what = document.kind === 'CHART' ? 'Prontuário' : 'Ficha';

  async function download() {
    setDownloading(true);
    try {
      const name = pdfName(what, clientName, 'assinado');
      saveFile(await recordsApi.documentFile(document.id), name);
      snackbar.success(`PDF assinado gerado: ${name}.`);
    } catch (error) {
      snackbar.failure(getApiError(error));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <article
      aria-label={`${what} assinado em ${formatDateTime(document.signedAt, timeZone)}`}
      className="grid gap-3 rounded-xl border border-surface-border bg-surface-elevated/40 p-4"
    >
      {document.method === 'DIGITAL' ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <figure className="grid gap-1">
            <SignatureImage strokes={document.clientStrokes ?? []} label="Assinatura do cliente" />
            <figcaption className="border-t border-surface-border pt-1 text-xs">
              <strong className="block text-sm text-content">{clientName}</strong>
              <span className="text-content-muted">Cliente</span>
            </figcaption>
          </figure>
          {document.professionalStrokes ? (
            <figure className="grid gap-1">
              <SignatureImage
                strokes={document.professionalStrokes}
                label="Assinatura do profissional"
              />
              <figcaption className="border-t border-surface-border pt-1 text-xs">
                <strong className="block text-sm text-content">
                  {document.professionalDisplayName}
                </strong>
                <span className="text-content-muted">Profissional responsável</span>
              </figcaption>
            </figure>
          ) : null}
        </div>
      ) : (
        <a
          href={recordsApi.documentFileUrl(document.id)}
          target="_blank"
          rel="noreferrer"
          className="flex w-fit max-w-full cursor-pointer items-center gap-3 rounded-xl border border-surface-border bg-surface-card px-3 py-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
        >
          <FileText className="size-5 shrink-0 text-danger" aria-hidden="true" />
          <span className="min-w-0">
            <span className="block break-all text-sm font-semibold text-brand-300 hover:underline">
              {document.file?.fileName}
            </span>
            <span className="text-xs text-content-muted">Abrir a via física assinada</span>
          </span>
        </a>
      )}
      <div className="grid gap-1 border-t border-surface-border pt-2 text-xs text-content-muted">
        <span>
          {document.method === 'DIGITAL' ? 'Assinado na tela' : 'Via física anexada'} em{' '}
          {formatDateTime(document.signedAt, timeZone)} · colhido por{' '}
          {document.collectedByDisplayName}
          {document.device ? ` · ${document.device}` : ''}
          {document.unitName ? ` · ${document.unitName}` : ''}
        </span>
        {document.legacy ? (
          <span>Assinatura anterior ao documento clínico: só o cliente assinou.</span>
        ) : null}
        <span className="break-all font-mono text-[0.7rem]">SHA-256 {document.contentSha256}</span>
        {document.intact ? (
          <span className="text-positive">O conteúdo confere com o que foi assinado.</span>
        ) : (
          <span className="font-semibold text-danger">
            O conteúdo não confere com o resumo gravado na assinatura.
          </span>
        )}
      </div>
      {document.method === 'DIGITAL' ? (
        <div>
          <Button size="sm" variant="outline" loading={downloading} onClick={() => void download()}>
            <FileDown className="size-4" />
            Baixar PDF assinado
          </Button>
        </div>
      ) : null}
    </article>
  );
}

/// Os documentos de uma ficha ou do prontuário e, para quem preenche, as três saídas: assinar na
/// tela, anexar ou fotografar a via do papel.
export function SignatureSection({
  scope,
  heading,
  documents,
  pendingText,
  pending,
  signable,
  clientName,
  title,
  timeZone,
  canManage,
  consents,
  onSigned,
}: {
  scope: ClinicalDocumentScope;
  heading: string;
  documents: readonly ClinicalDocumentSummary[];
  /// O aviso quando ainda não há documento próprio.
  pendingText: string | null;
  pending: boolean;
  /// Assinar na tela só vale para o estado atual; a via do papel pode ser de versão anterior.
  signable: { screen: boolean; paper: boolean };
  clientName: string;
  title: string;
  timeZone: string;
  canManage: boolean;
  consents?: ReactNode;
  onSigned: () => Promise<void>;
}) {
  const snackbar = useSnackbar();
  const input = useRef<HTMLInputElement>(null);
  const [signing, setSigning] = useState(false);
  const [camera, setCamera] = useState(false);
  const [uploading, setUploading] = useState(false);

  async function attach(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    try {
      await recordsApi.attachDocumentCopy(scope, file);
      await onSigned();
      snackbar.success('Via assinada anexada.');
    } catch (error) {
      snackbar.failure(getApiError(error));
    } finally {
      setUploading(false);
      if (input.current) input.current.value = '';
    }
  }

  const actions = canManage && (signable.screen || signable.paper);
  return (
    <section aria-label={heading} className="grid gap-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-content-faint">
        {heading}
      </h3>
      {documents.map((document) => (
        <DocumentCard
          key={document.id}
          document={document}
          clientName={clientName}
          timeZone={timeZone}
        />
      ))}
      {pendingText || actions ? (
        <div className="grid gap-3 rounded-xl border border-dashed border-surface-border p-4">
          {pendingText ? (
            <p className={cn('text-sm', pending ? 'text-warning' : 'text-content-muted')}>
              {pendingText}
            </p>
          ) : null}
          {actions ? (
            <p className="text-xs text-content-muted">
              Cliente e profissional assinam aqui na tela ou no papel: o PDF já sai com as duas
              linhas. Depois, anexe ou fotografe a via assinada.
            </p>
          ) : null}
          {actions ? (
            <div className="flex flex-wrap gap-2">
              {signable.screen ? (
                <Button size="sm" onClick={() => setSigning(true)}>
                  <PenLine className="size-4" />
                  Assinar na tela
                </Button>
              ) : null}
              {signable.paper ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    loading={uploading}
                    onClick={() => input.current?.click()}
                  >
                    <Paperclip className="size-4" />
                    Anexar via assinada
                  </Button>
                  {cameraAvailable() ? (
                    <Button size="sm" variant="outline" onClick={() => setCamera(true)}>
                      <Camera className="size-4" />
                      Fotografar via
                    </Button>
                  ) : null}
                  <input
                    ref={input}
                    type="file"
                    accept={ACCEPTED}
                    className="sr-only"
                    aria-label="Arquivo da via assinada"
                    onChange={(event) => void attach(event.target.files?.[0])}
                  />
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {signing ? (
        <SignDialog
          scope={scope}
          clientName={clientName}
          title={title}
          consents={consents}
          onClose={() => setSigning(false)}
          onSigned={async () => {
            await onSigned();
            setSigning(false);
            snackbar.success(`${title} assinado.`);
          }}
        />
      ) : null}
      <CameraCapture
        open={camera}
        onOpenChange={setCamera}
        title="Fotografar a via assinada"
        fileName="via-assinada.jpg"
        onCapture={(file) => void attach(file)}
      />
    </section>
  );
}

function useProfessionals(enabled = true) {
  return useQuery({
    queryKey: ['company', 'records', 'professionals'],
    queryFn: () => recordsApi.professionals(),
    enabled,
    retry: false,
  });
}

/// Com Agenda e profissionais na unidade, escolhe-se um deles; sem Agenda, quem colhe pode assinar
/// como profissional, e por padrão ninguém assina (ADR 0040, adendo de 2026-09-27).
function professionalOptions(data: DocumentProfessionals, blank: string) {
  return data.professionals.length
    ? data.professionals.map((item) => ({ value: item.id, label: item.displayName }))
    : [
        { value: BLANK, label: blank },
        { value: SELF, label: `${data.self} (eu)` },
      ];
}

function choiceOf(value: string): ProfessionalChoice {
  if (value === BLANK) return null;
  return value === SELF ? { self: true } : { resourceId: value };
}

/// O cliente e o profissional assinam nesta tela, cada pessoa na sua área. A área cresce pela alça ou
/// vai para a tela cheia, sem apagar o que já foi traçado.
function SignDialog({
  scope,
  clientName,
  title,
  consents,
  onClose,
  onSigned,
}: {
  scope: ClinicalDocumentScope;
  clientName: string;
  title: string;
  consents?: ReactNode;
  onClose: () => void;
  onSigned: () => Promise<void>;
}) {
  const professionals = useProfessionals();
  const [client, setClient] = useState<PadStroke[]>([]);
  const [professional, setProfessional] = useState<PadStroke[]>([]);
  const [chosen, setChosen] = useState<string>();
  const [pointer, setPointer] = useState<PadPointer | null>(null);
  const [full, setFull] = useState(false);
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const firstName = clientName.split(' ')[0];
  const data = professionals.data;
  const value = chosen ?? data?.suggestedId ?? data?.professionals[0]?.id ?? BLANK;
  const choice = choiceOf(value);
  const professionalName =
    value === SELF
      ? data?.self
      : data?.professionals.find((item) => item.id === value)?.displayName;

  async function submit() {
    const reviewed = reviewSignatureStrokes(client);
    if ('issue' in reviewed) {
      setError(`Assinatura do cliente: ${reviewed.issue}`);
      return;
    }
    let professionalStrokes: SignatureStroke[] | null = null;
    if (choice) {
      const second = reviewSignatureStrokes(professional);
      if ('issue' in second) {
        setError(`Assinatura do profissional: ${second.issue}`);
        return;
      }
      professionalStrokes = second.strokes;
    }
    setSaving(true);
    try {
      await recordsApi.signDocument({
        scope,
        clientStrokes: reviewed.strokes,
        professional: choice,
        professionalStrokes,
        pointer,
      });
      await onSigned();
    } catch (failure) {
      setError(getApiError(failure));
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      fullScreen={full}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`Assinar ${title}`}
      description={`${clientName} e o profissional assinam nesta tela.`}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button loading={saving} disabled={!data} onClick={() => void submit()}>
            Assinar
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <p className="rounded-xl border border-surface-border bg-surface-elevated/40 px-4 py-3 text-sm leading-6 text-content-secondary">
          Ao assinar, {firstName} confirma as informações de {title.toLowerCase()} como estão agora.
          Se a ficha for editada depois, a nova versão nasce sem assinatura e este documento
          continua valendo.
        </p>
        {consents}
        <Pad
          label={`Área de assinatura de ${clientName}`}
          heading={`Assinatura de ${clientName}`}
          strokes={client}
          full={full}
          onChange={(next, used) => {
            setClient(next);
            setPointer(used);
            setError(undefined);
          }}
        />
        {professionals.isError ? (
          <p role="alert" className="text-sm text-danger">
            {getApiError(professionals.error)}
          </p>
        ) : data ? (
          <div className="grid gap-3">
            <Select
              label={
                data.professionals.length ? 'Profissional responsável' : 'Assina como profissional'
              }
              value={value}
              options={professionalOptions(data, 'Ninguém: só o cliente assina')}
              onValueChange={(next) => {
                setChosen(next);
                setProfessional([]);
                setError(undefined);
              }}
              className="max-w-sm"
            />
            {choice ? (
              <Pad
                label={`Área de assinatura de ${professionalName ?? 'profissional'}`}
                heading={`Assinatura de ${professionalName ?? 'profissional'}`}
                strokes={professional}
                full={full}
                onChange={(next) => {
                  setProfessional(next);
                  setError(undefined);
                }}
              />
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-content-muted">Carregando profissionais…</p>
        )}
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-content-muted">
            {full
              ? 'Tela cheia. No celular, deite o aparelho.'
              : 'Arraste a borda de baixo para aumentar, ou use a tela cheia.'}{' '}
            Dedo, caneta do tablet ou mouse.
          </span>
          <Button size="sm" variant="outline" onClick={() => setFull(!full)}>
            {full ? <Minimize className="size-4" /> : <Expand className="size-4" />}
            {full ? 'Sair da tela cheia' : 'Tela cheia'}
          </Button>
        </div>
        <p className="text-xs text-content-faint">
          Guardamos o desenho das assinaturas, o resumo do conteúdo (SHA-256), quem colheu, data,
          hora e aparelho.
        </p>
      </div>
    </Modal>
  );
}

function Pad({
  label,
  heading,
  strokes,
  full,
  onChange,
}: {
  label: string;
  heading: string;
  strokes: PadStroke[];
  full: boolean;
  onChange: (strokes: PadStroke[], pointer: PadPointer | null) => void;
}) {
  const [height, setHeight] = useState(180);
  return (
    <div className="grid gap-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-content/70">{heading}</span>
        <Button size="sm" variant="ghost" onClick={() => onChange([], null)}>
          Limpar
        </Button>
      </div>
      <SignaturePad
        strokes={strokes}
        height={full ? 260 : height}
        onHeightChange={setHeight}
        label={label}
        onChange={onChange}
      />
    </div>
  );
}

/// Gerar o papel para assinar à mão: a linha do profissional sai com o nome escolhido ou em branco.
export function PrintButton({
  scope,
  fileParts,
  label = 'Gerar PDF',
}: {
  scope: ClinicalDocumentScope;
  fileParts: string[];
  label?: string;
}) {
  const snackbar = useSnackbar();
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<string>();
  const [exporting, setExporting] = useState(false);
  const professionals = useProfessionals(open);
  const data = professionals.data;
  const value = chosen ?? data?.suggestedId ?? BLANK;

  async function print() {
    setExporting(true);
    try {
      const name = pdfName(...fileParts);
      saveFile(await recordsApi.printPaper(scope, value === BLANK ? null : value), name);
      // O download não avisa nada no celular: o arquivo cai na pasta e a tela não muda.
      snackbar.success(`PDF gerado: ${name}.`);
      setOpen(false);
    } catch (error) {
      snackbar.failure(getApiError(error));
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <FileDown className="size-4" />
        {label}
      </Button>
      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Gerar PDF para assinar"
        description="O papel sai com o estado atual e as linhas do cliente e do profissional."
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button loading={exporting} disabled={!data} onClick={() => void print()}>
              Gerar PDF
            </Button>
          </>
        }
      >
        {professionals.isError ? (
          <p role="alert" className="text-sm text-danger">
            {getApiError(professionals.error)}
          </p>
        ) : data ? (
          <Select
            label="Profissional responsável"
            value={value}
            options={[
              { value: BLANK, label: 'Em branco, para escrever à mão' },
              ...(data.professionals.length
                ? data.professionals.map((item) => ({ value: item.id, label: item.displayName }))
                : [{ value: SELF, label: `${data.self} (eu)` }]),
            ]}
            onValueChange={setChosen}
          />
        ) : (
          <p className="text-sm text-content-muted">Carregando profissionais…</p>
        )}
      </Modal>
    </>
  );
}
